import { getProject } from '@/backend/getProject'
import type { RawAgent } from '@/data/Agents/types'
import { useAgentStore } from '@/data/Agents/useAgentStore'
import { serializeProject } from '@/modules/Projects/useProjects'
import { nonNullable } from '@/shared/utils/typeAssertions'
import { useQueries, useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue'

export function useAgentClient(props: { workspaceId: Ref<string> }) {
  const agentStore = useAgentStore()

  function fetch(agentId: MaybeRefOrGetter<string | undefined>) {
    return useQuery({
      queryKey: [props.workspaceId, 'agents', agentId],
      staleTime: 5_000,
      enabled: () => !!toValue(agentId),
      queryFn: async () => {
        const id = nonNullable(toValue(agentId))
        const result = await getProject(toValue(props.workspaceId), id)
        if (!result.ok) throw new Error(result.error.message)
        upsert(result.data)
        return agentStore.agents.get(id)
      },
    })
  }

  function fetchMultiple(agentIds: MaybeRefOrGetter<string[]>) {
    return useQueries({
      queries: computed(() =>
        toValue(agentIds).map((id) => ({
          queryKey: [props.workspaceId, 'agents', id],
          queryFn: async () => {
            const result = await getProject(toValue(props.workspaceId), id)
            if (!result.ok) throw new Error(result.error.message)
            upsert(result.data)
            return agentStore.agents.get(id)
          },
          staleTime: 5_000,
        })),
      ),
    })
  }

  function upsert(...agentList: RawAgent[]) {
    for (const rawAgent of agentList) {
      agentStore.agents.set(rawAgent.id, serializeProject(rawAgent))
    }
  }

  return { fetch, fetchMultiple }
}
